/**
 * Web Audio filter chain that gives an HTMLAudioElement a smooth
 * "far away, in a huge underground hall" effect. Lowpass-muffled
 * highs and a long convolver reverb tail. Used by the subsurface
 * region view to keep the player's music audible (rather than
 * pausing it) but recoloured so it feels distant and atmospheric.
 *
 * Per-audio-element chain:
 *
 *   source ──► lowpass ──► dryGain ──┐
 *                       │             ├─► masterGain ──► destination
 *                       └─► convolver ──► wetGain ──┘
 *
 * `setAmount(t, fadeMs)` ramps four AudioParams in parallel (lowpass
 * freq, wet, dry, master) over `fadeMs` to interpolate the chain
 * between "transparent" (t=0. No audible effect) and "deep cave"
 * (t=1. Heavily muffled + long reverb + quieter).
 *
 * The chain is wired up once on first attach; subsequent attaches
 * are no-ops that return the cached chain. The chain stays connected
 * to `destination` for the audio element's entire lifetime. Once an
 * HTMLAudioElement has been routed through `createMediaElementSource`
 * its native output is permanently captured by Web Audio, so the
 * graph cannot be safely torn down without disconnecting all sound.
 * `detachUnderwaterFilter(audio)` is therefore only safe to call when
 * the audio element itself is about to be disposed.
 */

let sharedCtx = null;
let sharedIR = null;
let warned = false;
const chains = new WeakMap();

function getCtx() {
  if (sharedCtx) {
    if (sharedCtx.state === 'suspended') sharedCtx.resume().catch(() => {});
    return sharedCtx;
  }
  const Ctor = typeof AudioContext !== 'undefined'
    ? AudioContext
    : (globalThis.webkitAudioContext || null);
  if (!Ctor) return null;
  try {
    sharedCtx = new Ctor();
    return sharedCtx;
  } catch {
    return null;
  }
}

/**
 * Procedurally-generated cave impulse response. 2.5 s of decaying
 * stereo noise with an exponential envelope. Sounds convincingly
 * like a large reverberant hall when convolved with music. The
 * buffer is built once and shared across every attached chain
 * (convolvers can share buffers safely).
 */
function buildIR(ctx) {
  const lengthSec = 2.5;
  const length = Math.floor(ctx.sampleRate * lengthSec);
  const buffer = ctx.createBuffer(2, length, ctx.sampleRate);
  // Exponent that gives a slightly steeper-than-linear decay so the
  // tail dies out smoothly without sounding like a flat noise gate.
  const decayPow = 2.5;
  for (let ch = 0; ch < 2; ch++) {
    const data = buffer.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      const t = i / length;
      const env = Math.pow(1 - t, decayPow);
      data[i] = (Math.random() * 2 - 1) * env;
    }
  }
  return buffer;
}

function getIR(ctx) {
  if (!sharedIR || sharedIR.sampleRate !== ctx.sampleRate) {
    sharedIR = buildIR(ctx);
  }
  return sharedIR;
}

function noopChain() {
  return { setAmount() {}, _noop: true };
}

/**
 * Wire an HTMLAudioElement through the underwater chain. Returns an
 * object with `setAmount(t, fadeMs)`. The chain is cached per audio
 * element so repeated calls during a session don't try to call
 * `createMediaElementSource` more than once (which would throw).
 *
 * Fails gracefully: in environments without Web Audio (older
 * browsers, test stubs) returns a no-op chain. Playback continues
 * unmodified.
 */
export function attachUnderwaterFilter(audio) {
  if (!audio) return noopChain();
  if (chains.has(audio)) return chains.get(audio);

  const ctx = getCtx();
  if (!ctx) return noopChain();

  let source;
  try {
    source = ctx.createMediaElementSource(audio);
  } catch (err) {
    // createMediaElementSource throws if the audio is already in
    // another Web Audio graph (shouldn't happen with our cache) or
    // if the audio's media is from a tainted cross-origin source
    // without CORS headers. Either way, log once and bail.
    if (!warned) {
      // eslint-disable-next-line no-console
      console.warn('[underwaterFilter] createMediaElementSource failed:', err);
      warned = true;
    }
    return noopChain();
  }

  const lowpass = ctx.createBiquadFilter();
  lowpass.type = 'lowpass';
  // 22 kHz is effectively above human hearing. Chain is inaudible
  // until setAmount drops the frequency.
  lowpass.frequency.value = 22000;
  lowpass.Q.value = 0.7;

  const convolver = ctx.createConvolver();
  convolver.buffer = getIR(ctx);

  const dryGain = ctx.createGain();
  dryGain.gain.value = 1;
  const wetGain = ctx.createGain();
  wetGain.gain.value = 0;
  const masterGain = ctx.createGain();
  masterGain.gain.value = 1;

  source.connect(lowpass);
  lowpass.connect(dryGain);
  lowpass.connect(convolver);
  convolver.connect(wetGain);
  dryGain.connect(masterGain);
  wetGain.connect(masterGain);
  masterGain.connect(ctx.destination);

  const chain = {
    audio,
    source,
    lowpass,
    convolver,
    dryGain,
    wetGain,
    masterGain,
    setAmount(amount, fadeMs = 0) {
      const t = Math.max(0, Math.min(1, amount));
      const ac = ctx;
      if (ac.state === 'suspended') ac.resume().catch(() => {});
      const now = ac.currentTime;
      const endTime = now + Math.max(0, fadeMs) / 1000;

      // Lowpass frequency. 22 kHz at t=0 (effectively bypass) →
      // 600 Hz at t=1 (heavy muffle. Only low-mids audible).
      const targetFreq = 22000 - t * (22000 - 600);
      lowpass.frequency.cancelScheduledValues(now);
      lowpass.frequency.setValueAtTime(lowpass.frequency.value, now);
      lowpass.frequency.linearRampToValueAtTime(targetFreq, endTime);

      // Wet (reverb tail). 0 at surface, 0.7 at full subsurface.
      wetGain.gain.cancelScheduledValues(now);
      wetGain.gain.setValueAtTime(wetGain.gain.value, now);
      wetGain.gain.linearRampToValueAtTime(0.7 * t, endTime);

      // Dry path stays partially audible so the music still has a
      // recognizable melody under the wash. 1.0 → 0.4 across t.
      dryGain.gain.cancelScheduledValues(now);
      dryGain.gain.setValueAtTime(dryGain.gain.value, now);
      dryGain.gain.linearRampToValueAtTime(1 - 0.6 * t, endTime);

      // Slight overall attenuation reinforces "this is far away".
      // 1.0 → 0.78 across t. The previous 0.7 made the subsurface
      // music a hair too quiet to track over the cave + underwater
      // bed; ~10% louder at full subsurface keeps the "distant hall"
      // feel while restoring audibility.
      masterGain.gain.cancelScheduledValues(now);
      masterGain.gain.setValueAtTime(masterGain.gain.value, now);
      masterGain.gain.linearRampToValueAtTime(1 - 0.22 * t, endTime);
    },
  };
  chains.set(audio, chain);
  return chain;
}

/**
 * Disconnect the underwater chain from `destination`. Safe to call
 * only when the audio element is about to be disposed. Once
 * disconnected, the audio element will produce NO sound until a new
 * chain is attached (createMediaElementSource captures audio output
 * permanently).
 */
// Whether an element is already routed through a chain. Lets callers skip
// attaching (createMediaElementSource forces the media pipeline through Web
// Audio permanently. a real memory cost on iOS) when the effect is off.
export function hasUnderwaterFilter(audio) {
  return !!audio && chains.has(audio);
}

export function detachUnderwaterFilter(audio) {
  if (!audio) return;
  const chain = chains.get(audio);
  if (!chain || chain._noop) {
    chains.delete(audio);
    return;
  }
  try { chain.source.disconnect(); } catch {}
  try { chain.lowpass.disconnect(); } catch {}
  try { chain.convolver.disconnect(); } catch {}
  try { chain.dryGain.disconnect(); } catch {}
  try { chain.wetGain.disconnect(); } catch {}
  try { chain.masterGain.disconnect(); } catch {}
  chains.delete(audio);
}
